// import external libraries
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { useDebounceValue } from 'usehooks-ts';

import { useShallow } from 'zustand/shallow';

// import hooks
import { useRosterIntervalDays } from 'components/shared/RosterViewDataGrid';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { refreshBacklogForEntities, useGetBacklog } from './Backlog.queries';
import { usePickedOrganizationalUnits } from 'hooks/usePickedOrganizationalUnits';
import { organizationalUnitCostCenterIds } from 'utils/organizationalUnits';
import { useBacklogPreferencesStore } from './Backlog.stores';

// import stores
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';

// import utils
import { groupRosterDaysByWeek } from 'components/shared/RosterViewDataGrid/hooks/useRosterTimelineColumns/utils';
import {
  buildBacklogRows,
  buildDisplayRows,
  filterBacklogRows,
  getCategoryRowKey,
  getTaskCategoryCounts,
  getWeekBuckets,
  getWeekDays
} from './components/BacklogGrid/BacklogGrid.utils';
import {
  categoryAppliesTo,
  missingRequiredFilter,
  toLegacyApiDateTime
} from './Backlog.utils';
import { getEightWeekInterval } from 'utils/date';

// import constants
import {
  BACKLOG_CATEGORY_DEBOUNCE_MS,
  CATEGORIES_FOLDED_BY_DEFAULT,
  IMPLEMENTED_CATEGORIES
} from './Backlog.consts';

// import types
import type { SingleOrRange } from 'components/shared/DateNavigation/DateNavigation.types';
import type {
  BacklogFilterState,
  BacklogTaskCategory,
  BacklogWeekScope
} from './Backlog.types';

/**
 * Refreshes the takenlijst after an appointment is edited through the reused afsprakenplanning drawer.
 * That drawer saves via SmartActionDialog, which refreshes the *calendar* (a no-op here), so the
 * takenlijst has to refresh itself. The drawer going from open to closed marks the end of an edit; a
 * cancel closes it too, so this over-refreshes rather than threading a save callback through
 * afsprakenplanning's shared form.
 *
 * That over-refresh is why it is a targeted refresh and not `invalidateQueries('backlog')`: the appointment
 * store is app-wide and persisted, so the open→closed transition also fires for a cancel and for an
 * appointment opened in a different view that was still in the store at mount — and a full invalidation
 * would answer each of those with a complete backlog fetch. Only the entities that appointment touches are
 * refetched, which for an unrelated appointment merges nothing and for a cancel returns the same rows.
 *
 * The entity ids are read while the drawer is open and kept in a ref, because closing clears `appointment`
 * and `original` in the same update. Both the ids the appointment was opened with and the ones currently in
 * the form are taken, so a participant added or removed during the edit is refreshed on both sides.
 */
export const useRefreshBacklogOnAppointmentClose = (): void => {
  const queryClient = useQueryClient();
  const openAppointmentEntityIds = useAppointmentStore(
    useShallow((state) => {
      if (!state.appointment) return null;
      const formEntityIds = Object.values(
        state.appointment.planEntities ?? {}
      ).flatMap((participants) =>
        (participants ?? []).map((participant) => participant?.entityId)
      );
      return Array.from(
        new Set(
          [...(state.original?.entityIds ?? []), ...formEntityIds].filter(
            (id): id is string => !!id
          )
        )
      );
    })
  );
  const lastOpenEntityIdsRef = useRef<string[] | null>(null);

  useEffect(() => {
    const lastOpenEntityIds = lastOpenEntityIdsRef.current;
    if (lastOpenEntityIds && !openAppointmentEntityIds) {
      // Fire-and-forget: nothing here can act on a failed refresh, and the planner still sees the list.
      refreshBacklogForEntities(queryClient, lastOpenEntityIds).catch(() => {});
    }
    lastOpenEntityIdsRef.current = openAppointmentEntityIds;
  }, [openAppointmentEntityIds, queryClient]);
};

export const useBacklog = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  // The category rows the planner has flipped away from their default fold state (see
  // CATEGORIES_FOLDED_BY_DEFAULT). Kept as "toggled" rather than "collapsed" so one set serves both a
  // default-open category (a toggle folds it) and a default-folded one (a toggle opens it).
  const [toggledFoldKeys, setToggledFoldKeys] = useState<Set<string>>(
    new Set()
  );
  // Every category that can actually report something starts selected.
  const [activeCategories, setActiveCategories] = useState<
    BacklogTaskCategory[]
  >(IMPLEMENTED_CATEGORIES);
  // Week mode is chosen from the date-picker's period menu; it shows the week-detail for the first week
  // of the window (and follows the window as the planner navigates). Clicking an outside-treatment cell
  // opens one specific week on top of that, until the period or window changes.
  const [weekMode, setWeekMode] = useState(false);
  const [explicitWeek, setExplicitWeek] = useState<string | null>(null);
  // The date-picker owns its period in this shared store; setting it keeps the picker's menu in step
  // when the view is opened from a cell rather than from the menu itself.
  const setDateNavigationPeriod = useDateNavigationStore(
    (state) => state.setPeriod
  );

  // The filters and the three view settings are remembered across visits, so a planner does not
  // set them again each time the page opens. Their defaults live in the store: no narrowing filters,
  // the signed difference in the cells, and both the totals column and the fulfilled weeks shown.
  const {
    rowEntityType,
    organizationalUnitIds,
    entityFilters,
    employeeViewFilters,
    cellValueMode,
    showTotals,
    showCompleted,
    setRowEntityType,
    setEntityFilters,
    setEmployeeViewFilters,
    setOrganizationalUnitIds,
    setCellValueMode,
    setShowTotals,
    setShowCompleted
  } = useBacklogPreferencesStore(
    useShallow((state) => ({
      rowEntityType: state.rowEntityType,
      organizationalUnitIds: state.organizationalUnitIds,
      entityFilters: state.entityFilters,
      employeeViewFilters: state.employeeViewFilters,
      cellValueMode: state.cellValueMode,
      showTotals: state.showTotals,
      showCompleted: state.showCompleted,
      setRowEntityType: state.setRowEntityType,
      setEntityFilters: state.setEntityFilters,
      setEmployeeViewFilters: state.setEmployeeViewFilters,
      setOrganizationalUnitIds: state.setOrganizationalUnitIds,
      setCellValueMode: state.setCellValueMode,
      setShowTotals: state.setShowTotals,
      setShowCompleted: state.setShowCompleted
    }))
  );

  // A tile the row type cannot carry is greyed out, so it must not be asked for either — the backend would
  // run its detection for rows that cannot exist. Filtered before the debounce rather than after, so
  // switching view does not send the old view's categories once on the way through.
  //
  // Memoized because useDebounceValue below compares by identity: a fresh array every render would leave
  // it forever unequal to its own last value, so it would re-settle on a new reference every debounce
  // interval and re-render the whole page for as long as the takenlijst is open.
  const applicableCategories = useMemo(
    () =>
      activeCategories.filter((category) =>
        categoryAppliesTo(category, rowEntityType)
      ),
    [activeCategories, rowEntityType]
  );

  // What the tiles ask the backend for, settled. The tiles themselves stay instant: `activeCategories`
  // still drives the rows and the tile styling without delay, so only the request waits.
  const [requestedCategories] = useDebounceValue(
    applicableCategories,
    BACKLOG_CATEGORY_DEBOUNCE_MS
  );

  // The remembered plan unit ids as the plan units themselves. Resolved against the plan units the app
  // has loaded, so one that no longer exists simply drops out rather than scoping to something gone.
  const {
    organizationalUnits: pickedOrganizationalUnits,
    isAwaitingOptions: isAwaitingOrganizationalUnits
  } = usePickedOrganizationalUnits(organizationalUnitIds);

  // The scope the view still needs before it asks anything: a plan unit, or the person the planner names.
  // The page turns this into an empty state naming what to pick.
  const requiredFilter = missingRequiredFilter({
    rowEntityType,
    entityFilters,
    organizationalUnitIds
  });

  // The applied plan units as the cost-centre ids the request carries. Resolved here rather than in the
  // popover, which the request must not depend on being opened; the popover resolves its own draft.
  const organizationalUnitIdsForRequest = useMemo(
    () => organizationalUnitCostCenterIds(pickedOrganizationalUnits),
    [pickedOrganizationalUnits]
  );

  const dateInterval = useMemo(
    () => getEightWeekInterval(selectedDate),
    [selectedDate]
  );

  const {
    data: backlog,
    isLoading,
    isError,
    refetch
  } = useGetBacklog({
    startDate: dateInterval.startDate,
    endDate: dateInterval.endDate,
    // Deviating weeks only, unless the planner asks to see the fulfilled ones as well.
    includeAllSummaries: showCompleted,
    entityFilters,
    organizationalUnitIds: organizationalUnitIdsForRequest,
    activeCategories: requestedCategories,
    rowEntityType,
    // Held for the plan units too, on every view: asking before the picks resolve would fetch the
    // unscoped — far heavier — question and then have to ask again. `isAwaitingOptions` is already false
    // when nothing is picked, so this holds nothing that does not depend on it.
    isRequestHeld: requiredFilter !== null || isAwaitingOrganizationalUnits
  });

  const { rosterDays: intervalRosterDays } = useRosterIntervalDays({
    defaultInterval: dateInterval
  });

  // The rows are built here rather than in the grid so the page can tell "no data" apart from
  // "nothing left after filtering".
  const { rosterDays, weeks } = useMemo(() => {
    const groupedWeeks = groupRosterDaysByWeek(intervalRosterDays);
    return { rosterDays: groupedWeeks.flat(), weeks: groupedWeeks };
  }, [intervalRosterDays]);

  // Only what still narrows the rows here: the entity and activity picks are resolved by the backend, so
  // the response is already scoped to them by the time it reaches this.
  const filters: BacklogFilterState = useMemo(
    () => ({ activeCategories, showCompleted }),
    [activeCategories, showCompleted]
  );

  const allRows = useMemo(
    () => buildBacklogRows(backlog, weeks),
    [backlog, weeks]
  );

  // Counted over the server-scoped rows, before the tile filter, so each tile shows what selecting
  // it would bring in.
  // The week actually shown: a clicked week wins; otherwise, in week mode, the window's first week.
  const openWeek = useMemo(() => {
    if (explicitWeek) return explicitWeek;
    if (!weekMode) return null;
    return getWeekBuckets(weeks)[0]?.field ?? null;
  }, [explicitWeek, weekMode, weeks]);

  // The open week as the tile counts read it: its column, and its seven dates for the overlap
  // occurrences, which are identified by date rather than by column.
  const weekScope: BacklogWeekScope | null = useMemo(() => {
    if (!openWeek) return null;
    return {
      field: openWeek,
      dates: new Set(
        getWeekDays(weeks, openWeek).map((day) =>
          dayjs(day.date).format('YYYY-MM-DD')
        )
      )
    };
  }, [openWeek, weeks]);

  // What the date picker shows and pages from. In the eight-week grid that is the window's own anchor,
  // but once a week is open it has to be that week — otherwise the toolbar keeps naming the window's
  // first week while the planner is looking at another one, and the arrows step from the wrong place.
  //
  // It is only what the picker *shows*; what an arrow then does with it is `onDateChange`'s to decide, and
  // inside an opened window that is to page the week rather than re-anchor the eight.
  const toolbarDate = useMemo(() => {
    if (!openWeek) return selectedDate;
    const bucket = getWeekBuckets(weeks).find(
      ({ field }) => field === openWeek
    );
    return bucket ? new Date(bucket.start) : selectedDate;
  }, [openWeek, weeks, selectedDate]);

  const categoryCounts = useMemo(
    () => getTaskCategoryCounts(allRows, weekScope),
    [allRows, weekScope]
  );

  const ruleRows = useMemo(
    () => filterBacklogRows(allRows, filters),
    [allRows, filters]
  );

  // The actual collapsed set, derived from the planner's toggles and each category's default fold
  // state. A default-folded category (Tekort/Overschot) is collapsed unless it was opened; every other
  // category is collapsed only once it was folded. Deriving it — rather than seeding state per entity —
  // keeps new entities (after a period or filter change) folded too.
  const collapsedCategoryKeys = useMemo(() => {
    const foldedByDefault = new Set(CATEGORIES_FOLDED_BY_DEFAULT);
    const collapsed = new Set<string>();
    allRows.forEach((row) => {
      const key = getCategoryRowKey(row.entityId, row.category);
      const toggled = toggledFoldKeys.has(key);
      if (foldedByDefault.has(row.category) ? !toggled : toggled) {
        collapsed.add(key);
      }
    });
    return collapsed;
  }, [allRows, toggledFoldKeys]);

  // Header rows are added after filtering, so a category whose every rule was filtered out does not
  // leave an empty header behind.
  const rows = useMemo(
    () => buildDisplayRows(ruleRows, weeks, collapsedCategoryKeys),
    [ruleRows, weeks, collapsedCategoryKeys]
  );

  // The arrows page whatever the picker is showing. In the eight-week grid that is the window's anchor, so
  // they move the window. With a week open they page that week — and the eight weeks behind it stay where
  // they are: they are the period the planner drilled in from, and re-anchoring them on every arrow press
  // loses it, so closing the detail would land somewhere other than where they came from.
  //
  // Stepping past the window's edge does move it, because there is no week left inside to open. The new
  // window is anchored on the week stepped to, which then becomes its first week — the same thing a period
  // change has always done.
  const onDateChange = useCallback(
    (date: SingleOrRange | null) => {
      if (!date) return;
      const raw = Array.isArray(date) ? date[0] : date;
      if (!raw) return;
      const next = dayjs.isDayjs(raw) ? raw.toDate() : raw;

      if (openWeek) {
        const at = dayjs(next).valueOf();
        const bucket = getWeekBuckets(weeks).find(
          ({ start, end }) => at >= start && at <= end
        );
        if (bucket) {
          setExplicitWeek(bucket.field);
          return;
        }
      }

      setSelectedDate(next);
    },
    [openWeek, weeks]
  );

  const onToggleCategoryRow = useCallback(
    (entityId: string, category: BacklogTaskCategory) => {
      const key = getCategoryRowKey(entityId, category);
      setToggledFoldKeys((previous) => {
        const next = new Set(previous);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    []
  );

  const onToggleCategory = useCallback((category: BacklogTaskCategory) => {
    setActiveCategories((previous) =>
      previous.includes(category)
        ? previous.filter((active) => active !== category)
        : [...previous, category]
    );
  }, []);

  // Toggles only what this view can report on, the same set the All tile reads itself by. The picks for the
  // other views are kept: a planner who switches back finds them as they left them, and clearing everything
  // here would take those with it.
  const onToggleAll = useCallback(() => {
    const applicable = IMPLEMENTED_CATEGORIES.filter((category) =>
      categoryAppliesTo(category, rowEntityType)
    );
    setActiveCategories((previous) => {
      const kept = previous.filter(
        (category) => !applicable.includes(category)
      );
      const allOn = applicable.every((category) => previous.includes(category));
      return allOn ? kept : [...kept, ...applicable];
    });
  }, [rowEntityType]);

  // Clicking a cell opens that specific week and enters week mode, so paging the date afterwards stays
  // in the week-detail (on the new window's week) rather than dropping back to the grid. It also sets
  // the date-picker's own period, so its menu reflects "week" the way choosing it from there would.
  const onOpenWeek = useCallback(
    (weekField: string) => {
      setExplicitWeek(weekField);
      setWeekMode(true);
      setDateNavigationPeriod('week');
    },
    [setDateNavigationPeriod]
  );

  const onCloseWeek = useCallback(() => {
    setExplicitWeek(null);
    setWeekMode(false);
    setDateNavigationPeriod('eightWeeks');
  }, [setDateNavigationPeriod]);

  // The date-picker's period menu drives the view: 'week' opens the week-detail, 'eightWeeks' (or any
  // other) returns to the grid. Stable so the DateNavigation effect that reports the period does not
  // loop. A clicked week survives the 'week' report (it also sets the period), so only leaving week mode
  // clears it.
  const onPeriodChange = useCallback((period: string) => {
    setWeekMode(period === 'week');
    if (period !== 'week') setExplicitWeek(null);
  }, []);

  // A window change drops the one clicked week (it no longer exists), but keeps week mode — so paging
  // through the weeks stays in the week-detail, on the new window's first week.
  useEffect(() => {
    setExplicitWeek(null);
  }, [dateInterval]);

  return {
    selectedDate,
    onDateChange,
    rowEntityType,
    onRowEntityTypeChange: setRowEntityType,
    requiredFilter,
    organizationalUnitIds,
    onOrganizationalUnitIdsChange: setOrganizationalUnitIds,
    employeeViewFilters,
    onEmployeeViewFiltersChange: setEmployeeViewFilters,
    // The window the grid is showing, for the filter lists that are asked per period. A full datetime, not
    // a date — see toLegacyApiDateTime.
    startDate: toLegacyApiDateTime(dateInterval.startDate),
    endDate: toLegacyApiDateTime(dateInterval.endDate),
    entityFilters,
    onEntityFiltersChange: setEntityFilters,
    categoryCounts,
    activeCategories,
    onToggleCategory,
    onToggleAll,
    onToggleCategoryRow,
    cellValueMode,
    setCellValueMode,
    showTotals,
    setShowTotals,
    showCompleted,
    setShowCompleted,
    rosterDays,
    rows,
    // The week-detail reuses the same filtered data as the weeks grid: the raw response (for events),
    // the filtered rule rows (for the deviation values), the active tiles and the collapse state — so
    // tiles, filters and view settings apply there too.
    backlog,
    weeks,
    ruleRows,
    collapsedCategoryKeys,
    openWeek,
    toolbarDate,
    onOpenWeek,
    onCloseWeek,
    onPeriodChange,
    // Waiting for the plan units to resolve is loading too: the request is held until they land, and a
    // disabled query reports neither loading nor data — which would flash the "nothing deviates" state.
    isLoading: isLoading || isAwaitingOrganizationalUnits,
    isError,
    refetch
  };
};
